import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAIL_SPLIT_MAX_LIST,
  MAIL_SPLIT_MAX_NAV,
  MAIL_SPLIT_MIN_LIST,
  MAIL_SPLIT_MIN_NAV,
  MAIL_SPLIT_MIN_READING,
  MAIL_SPLIT_RESIZER_PX,
  persistMailSplitLayout,
  readMailSplitLayout,
  type MailSplitLayout,
} from "@/lib/mail/mailSplitLayoutPrefs";

type DragTarget = "nav" | "list";

type DragState = {
  target: DragTarget;
  pointerId: number;
  startX: number;
  startNav: number;
  startList: number;
};

const WINDOW_RESIZE_DEBOUNCE_MS = 200;
const WINDOW_RESIZE_MIN_DELTA_PX = 16;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function maxListWidth(totalWidth: number, navWidth: number) {
  return totalWidth - navWidth - MAIL_SPLIT_MIN_READING - MAIL_SPLIT_RESIZER_PX * 2;
}

type Options = {
  /** Ne pas recalculer les colonnes (modale compose ouverte — évite faux resize iframe). */
  freezeLayout?: boolean;
};

export function useMailSplitLayout(options: Options = {}) {
  const freezeLayout = options.freezeLayout ?? false;
  const freezeLayoutRef = useRef(freezeLayout);
  freezeLayoutRef.current = freezeLayout;
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const layoutRef = useRef<MailSplitLayout>(readMailSplitLayout());
  const [layout, setLayout] = useState<MailSplitLayout>(() => readMailSplitLayout());
  const lastRootWidthRef = useRef(0);

  layoutRef.current = layout;

  const measureRootWidth = useCallback(() => {
    return rootRef.current?.getBoundingClientRect().width ?? 0;
  }, []);

  const applyLayout = useCallback((next: MailSplitLayout) => {
    const root = rootRef.current;
    if (root) {
      const total = root.getBoundingClientRect().width;
      const listCap = maxListWidth(total, next.navWidth);
      next = {
        navWidth: clamp(next.navWidth, MAIL_SPLIT_MIN_NAV, MAIL_SPLIT_MAX_NAV),
        listWidth: clamp(next.listWidth, MAIL_SPLIT_MIN_LIST, Math.min(MAIL_SPLIT_MAX_LIST, listCap)),
      };
    }
    layoutRef.current = next;
    setLayout(next);
  }, []);

  const endDrag = useCallback(() => {
    if (!dragRef.current) return;
    dragRef.current = null;
    document.body.classList.remove("mail-split-dragging");
    persistMailSplitLayout(layoutRef.current);
  }, []);

  const clampListToViewport = useCallback(() => {
    if (freezeLayoutRef.current || dragRef.current) return;

    const root = rootRef.current;
    if (!root) return;

    const total = root.getBoundingClientRect().width;
    const current = layoutRef.current;
    const listCap = maxListWidth(total, current.navWidth);
    if (current.listWidth > listCap) {
      applyLayout({
        ...current,
        listWidth: Math.max(MAIL_SPLIT_MIN_LIST, listCap),
      });
    }
  }, [applyLayout]);

  useEffect(() => {
    if (freezeLayout) return;

    const width = measureRootWidth();
    if (width > 0) {
      lastRootWidthRef.current = width;
    }
    clampListToViewport();
  }, [clampListToViewport, freezeLayout, measureRootWidth]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      const root = rootRef.current;
      if (!drag || drag.pointerId !== e.pointerId || !root) return;

      const total = root.getBoundingClientRect().width;
      const dx = e.clientX - drag.startX;
      const current = layoutRef.current;

      if (drag.target === "nav") {
        const navWidth = clamp(drag.startNav + dx, MAIL_SPLIT_MIN_NAV, MAIL_SPLIT_MAX_NAV);
        const listCap = maxListWidth(total, navWidth);
        applyLayout({
          navWidth,
          listWidth: clamp(current.listWidth, MAIL_SPLIT_MIN_LIST, Math.min(MAIL_SPLIT_MAX_LIST, listCap)),
        });
        return;
      }

      const listCap = maxListWidth(total, current.navWidth);
      applyLayout({
        navWidth: current.navWidth,
        listWidth: clamp(drag.startList + dx, MAIL_SPLIT_MIN_LIST, Math.min(MAIL_SPLIT_MAX_LIST, listCap)),
      });
    };

    const onEnd = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      endDrag();
    };

    const onLostCapture = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      endDrag();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    window.addEventListener("lostpointercapture", onLostCapture);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      window.removeEventListener("lostpointercapture", onLostCapture);
    };
  }, [applyLayout, endDrag]);

  useEffect(() => {
    let timer: number | undefined;

    const onResize = () => {
      if (freezeLayoutRef.current || dragRef.current) return;

      const nextWidth = measureRootWidth();
      if (nextWidth <= 0) return;
      if (
        lastRootWidthRef.current > 0 &&
        Math.abs(nextWidth - lastRootWidthRef.current) < WINDOW_RESIZE_MIN_DELTA_PX
      ) {
        return;
      }
      lastRootWidthRef.current = nextWidth;

      if (timer != null) {
        window.clearTimeout(timer);
      }
      timer = window.setTimeout(() => {
        clampListToViewport();
      }, WINDOW_RESIZE_DEBOUNCE_MS);
    };

    lastRootWidthRef.current = measureRootWidth();
    window.addEventListener("resize", onResize);
    return () => {
      if (timer != null) {
        window.clearTimeout(timer);
      }
      window.removeEventListener("resize", onResize);
    };
  }, [clampListToViewport, measureRootWidth]);

  const startResize = useCallback((target: DragTarget) => {
    return (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      const current = layoutRef.current;
      dragRef.current = {
        target,
        pointerId: e.pointerId,
        startX: e.clientX,
        startNav: current.navWidth,
        startList: current.listWidth,
      };
      document.body.classList.add("mail-split-dragging");
    };
  }, []);

  return {
    rootRef,
    layout,
    startNavResize: startResize("nav"),
    startListResize: startResize("list"),
  };
}
